import type { SettingsRegistryEntry } from "@magic-md/engine-client/config";
import { getStaticMessageKey, type LocaleParams } from "@magic-md/i18n";
import { useI18n } from "@magic-md/i18n/react";
import { useMemo } from "react";

export type SettingsTextResolver = (
    value?: string | null,
    fallback?: string,
    params?: LocaleParams,
) => string;

export function useSettingsTextResolver(): SettingsTextResolver {
    const i18n = useI18n();
    return useMemo(
        () => (value?: string | null, fallback = "", params?: LocaleParams) => {
            if (!value) return fallback;
            const resolved = i18n.resolveStaticText(value, params);
            if (resolved === value && getStaticMessageKey(value) && fallback) {
                return fallback;
            }
            return resolved;
        },
        [i18n],
    );
}

export function settingsEntryTitle(
    entry: SettingsRegistryEntry,
    resolveText: SettingsTextResolver,
): string {
    const fallback = resolveText(entry.title, entry.key, entry.titleParams);
    const builtinKey = editorSettingMessageRef(entry, "title");
    return builtinKey ? resolveText(builtinKey, fallback, entry.titleParams) : fallback;
}

export function settingsEntryDescription(
    entry: SettingsRegistryEntry,
    resolveText: SettingsTextResolver,
): string | null {
    const fallback = entry.description
        ? resolveText(entry.description, "", entry.descriptionParams)
        : "";
    const builtinKey = editorSettingMessageRef(entry, "description");
    const resolved = builtinKey
        ? resolveText(builtinKey, fallback, entry.descriptionParams)
        : fallback;
    return resolved || null;
}

function editorSettingMessageRef(
    entry: SettingsRegistryEntry,
    field: "title" | "description",
): string | null {
    if (entry.owner === "editor" && entry.key.startsWith("editor.")) {
        return `%editor.settings.${entry.key.slice("editor.".length)}.${field}%`;
    }

    if (
        (entry.owner === "engine" && entry.key.startsWith("engine.")) ||
        (entry.owner === "gateway" && entry.key.startsWith("gateway."))
    ) {
        return `%editor.settings.${entry.key}.${field}%`;
    }

    return null;
}
